// Help center article copy (from the PropFunding Help Center FAQ docs).
// `body` blocks: a string is a paragraph; { list }, { callout } and { term, text } render
// as bullets, a highlighted note and a labelled definition. **text** marks bold.

const ARTICLE_COPY = {
  Portfolio: [
    {
      title: "What does my Portfolio show?",
      body: [
        "Your Portfolio is the main dashboard for your funded account.",
        "Here you can track your PRP balance and equity, profit and loss, daily loss usage, maximum drawdown, withdrawal eligibility, Gold unlock progress and account status.",
        "Your Portfolio updates as you trade so you can see where your account stands at a glance.",
      ],
    },
    {
      title: "What are Balance and Equity?",
      body: [
        "**Balance** is your account value after closed trades.",
        "**Equity** includes the profit or loss from trades that are still open.",
        "Because your loss limits are monitored using equity, open positions can affect your account limits before a trade is closed.",
      ],
    },
    {
      title: "How does the 20% maximum loss work?",
      body: [
        "Your maximum loss is **20% static**, based on your original 10,000 PRP starting balance.",
        "That means your maximum-loss level stays fixed at **8,000 PRP**. It does not move higher when your account grows.",
        "If your equity reaches the maximum-loss level, the account is automatically disabled.",
        { callout: "10,000 PRP starting balance → 8,000 PRP static maximum-loss level." },
      ],
    },
    {
      title: "How does the 5% daily loss limit work?",
      body: [
        "Your funded account has a **5% daily loss limit**.",
        "The limit is monitored using your account equity, so both closed losses and losses on open trades can affect it.",
        "If your equity reaches the daily-loss limit, the account is automatically disabled.",
        "Your Portfolio shows your current daily-loss usage so you can monitor it while trading.",
      ],
    },
    {
      title: "What is PRP and how are my profits valued?",
      body: [
        "PRP is the proprietary unit used to measure your funded account.",
        "Your balance, PnL and account limits are shown in PRP.",
        "**1 PRP = 0.50 USDC.**",
        "Eligible PRP profit converts to USDC at this fixed rate when withdrawn.",
        "You keep **100% of your eligible PRP profit** at the fixed redemption rate.",
        { callout: "Example: 500 PRP profit = 250 USDC." },
      ],
    },
    {
      title: "When can I withdraw my profits?",
      body: [
        "Your first standard withdrawal becomes available **14 days after your funded account begins**.",
        {
          list: [
            "Minimum withdrawal: **2% of starting balance**.",
            "Maximum withdrawal: **20% of starting balance per 30-day payout cycle**.",
            "Redemption rate: **1 PRP = 0.50 USDC**.",
          ],
        },
        { callout: "Your Portfolio will show when you are eligible to request a withdrawal." },
      ],
    },
    {
      title: "What happens when I reach the 20% payout cap?",
      body: [
        "You can withdraw up to **20% of your starting account balance during each 28-day payout cycle**.",
        "Your payout cycle begins when you make your **first withdrawal**.",
        "Once your cumulative withdrawals reach the 20% cap:",
        {
          list: [
            "Your account is **temporarily paused** for 14 days.",
            "No new trades can be opened.",
            "Any profit generated above the 20% cap is **not eligible for payout**.",
          ],
        },
        {
          callout:
            "Example: If you withdraw 10% on October 1 and another 10% on October 15, you have reached the 20% cap. Your account remains paused until October 29th, after which trading can resume.",
        },
      ],
    },
  ],
  Leaderboard: [
    {
      title: "How does the Leaderboard work?",
      body: [
        "The Leaderboard ranks funded PropFunding traders by their performance.",
        "You can see each trader's:",
        { list: ["Rank", "PRP profit", "USDC paid", "Daily drawdown", "Maximum drawdown", "Account status"] },
        { callout: "Leaderboard data updates every few minutes." },
      ],
    },
    {
      title: "How is my Leaderboard rank calculated?",
      body: [
        "Your rank is based on the **cumulative eligible PRP profit generated on your funded account**.",
        "Withdrawing profit does not reduce your ranking. Your performance stays recorded even after a successful withdrawal.",
        "For example, if you earn and withdraw 1,000 PRP, that 1,000 PRP still counts toward your Leaderboard performance.",
      ],
    },
    {
      title: "What does USDC Paid mean?",
      body: [
        "**USDC Paid** shows the total amount successfully redeemed and paid to that trader from their funded account.",
        "PRP profits are converted using the fixed rate:",
        "**1 PRP = 0.50 USDC**",
        "This figure reflects completed payouts, not pending withdrawal requests.",
      ],
    },
    {
      title: "What do Daily DD and Max DD show?",
      body: [
        "**Daily DD** shows how much of the trader's **5% daily loss limit** is currently being used.",
        "**Max DD** shows how much of the trader's **20% static maximum loss limit** has been used.",
        "These figures help show performance alongside the amount of risk being taken.",
        { callout: "Example: Daily DD 1.2% / 5% | Max DD 4.5% / 20%" },
      ],
    },
    {
      title: "What do the account statuses mean?",
      body: [
        { term: "Active", text: "The funded account is active and available for trading." },
        { term: "Paused", text: "Trading is temporarily unavailable, such as when the trader has reached their payout-cycle cap." },
        { term: "Breached", text: "The account reached a trading loss limit and has been disabled." },
      ],
    },
    {
      title: "Why does my Leaderboard position change?",
      body: [
        "Leaderboard rankings change as funded traders generate new profits.",
        "Your position may move up or down as other traders perform, even if your own cumulative profit has not changed.",
        "The Leaderboard refreshes regularly so rankings reflect the latest recorded performance.",
      ],
    },
  ],
  Withdrawals: [
    {
      title: "When can I make my first withdrawal?",
      body: [
        "Your first standard withdrawal becomes available **14 days after your funded account begins**.",
        "The qualification redemption you received when activating your account is separate from this 14-day withdrawal period.",
        "Once eligible, your Portfolio will show that withdrawals are available.",
      ],
    },
    {
      title: "How much can I withdraw?",
      body: [
        "Each withdrawal must be at least **2% of your starting account balance**.",
        "For a 10,000 PRP account, the minimum withdrawal is **200 PRP**.",
        "You can withdraw up to **20% of your starting balance during each 30-day payout cycle**.",
        "For a 10,000 PRP account, the maximum per cycle is **2,000 PRP**.",
      ],
    },
    {
      title: "How does the 14-day payout cycle work?",
      body: [
        "Your first 14-day payout window begins when you make your first standard withdrawal. A new payout window opens 14 days after each payout request.",
        "Across each 28-day cycle, you may make multiple withdrawals, provided your cumulative withdrawals do not exceed 20% of your starting balance.",
        {
          callout:
            "Example: Withdraw 1,000 PRP on October 1, then another 1,000 PRP on October 15. You've now reached the 2,000 PRP payout cap, so your account is paused for 14 days before trading resumes.",
        },
      ],
    },
    {
      title: "What happens when I reach the 20% payout cap?",
      body: [
        "Once your cumulative withdrawals reach the 20% cap:",
        {
          list: [
            "Your account is **temporarily paused**.",
            "No new trades can be opened for 14 days.",
            "Any profit generated above the payout cap is **not eligible for withdrawal**.",
          ],
        },
        { callout: "Once the 14 days are up, your account can resume trading." },
      ],
    },
    {
      title: "How are my PRP profits converted to USDC?",
      body: [
        "All eligible withdrawals use the fixed redemption rate:",
        "**1 PRP = 0.50 USDC**",
        "The conversion rate does not change based on market conditions or Withdrawal Pool size.",
        { list: ["200 PRP = 100 USDC", "500 PRP = 250 USDC", "2,000 PRP = 1,000 USDC"] },
      ],
    },
    {
      title: "Does withdrawing reduce my Leaderboard performance?",
      body: [
        "No.",
        "Your Leaderboard ranking is based on your **cumulative eligible PRP profit**, not your current account balance.",
        "If you earn and withdraw 1,000 PRP, that performance remains recorded on your Leaderboard profile.",
      ],
    },
    {
      title: "Why might my withdrawal be unavailable?",
      body: [
        "A withdrawal may not be available if:",
        {
          list: [
            "Your funded account has not yet reached its first 14-day withdrawal date.",
            "Your requested amount is below the 2% minimum.",
            "You have reached your 20% payout cap for the current cycle.",
            "Your account is paused or disabled.",
            "The profit is not eligible for redemption under the program rules.",
          ],
        },
        { callout: "Your Portfolio will show your current withdrawal eligibility and payout-cycle status." },
      ],
    },
  ],
  Referrals: [
    {
      title: "How does the Referral Program work?",
      body: [
        "Share your personal referral link with other traders.",
        "They can start their 10,000 PRP account for free. You earn a referral commission only when they qualify and successfully pay their activation fee.",
        { callout: "Free signup = no commission | Paid activation = commission earned" },
      ],
    },
    {
      title: "How much do I earn per referral?",
      body: [
        "You earn **$25 for every referred trader who successfully activates a paid account**.",
        "Referral rewards can be withdrawn once your available balance reaches **$125**.",
        "Your referral dashboard shows:",
        { list: ["Referred traders", "Paid activations", "Pending commission", "Available commission", "Total earnings"] },
      ],
    },
    {
      title: "When is a referral counted as mine?",
      body: [
        "A trader must register through your unique referral link for the referral to be attributed to you.",
        "Once linked to your account, their qualification attempts remain connected to your referral.",
        "If they qualify and activate within the referral attribution period, you receive the commission.",
      ],
    },
    {
      title: "When does my referral commission become available?",
      body: [
        "Commission is earned once the referred trader:",
        {
          list: [
            "Completes KYC",
            "Successfully pays the activation fee",
            "Passes the applicable payment and fraud checks",
          ],
        },
        {
          callout:
            "Commissions may remain pending for 14 days during the chargeback or payment-review period before becoming available.",
        },
      ],
    },
    {
      title: "Do I earn anything when someone starts for free?",
      body: [
        "No.",
        "There is no commission for:",
        { list: ["Free registrations", "Starting to trade", "Reaching +1%", "Completing KYC alone"] },
        { callout: "You earn only when the referred trader becomes a paid activated customer." },
      ],
    },
    {
      title: "What referral activity is not allowed?",
      body: [
        "Referral commissions may be rejected for:",
        {
          list: [
            "Self-referrals",
            "Duplicate or false identities",
            "Creating accounts to generate referral commissions",
            "Payment manipulation or chargebacks",
            "Misleading promotions",
            "Attempts to bypass referral or account limits",
          ],
        },
        {
          callout:
            "Abusive referral activity may result in commissions being cancelled and referral access being restricted.",
        },
      ],
    },
    {
      title: "How do I withdraw my referral rewards?",
      body: [
        "Referral rewards can be withdrawn directly from your portal once your available balance reaches **$125**.",
        {
          list: ["Minimum withdrawal: **$125**", "Payout currency: **USDC**", "Supported network: **ERC-20 only**"],
        },
        {
          callout:
            "Make sure the wallet address you enter supports USDC on the Ethereum network (ERC-20). PropFunding is not responsible for funds sent to an unsupported network.",
        },
      ],
    },
  ],
};

export const HELP_CATEGORIES = Object.keys(ARTICLE_COPY);

export const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const HELP_ARTICLES = HELP_CATEGORIES.flatMap((category) =>
  ARTICLE_COPY[category].map((article) => ({ ...article, category, slug: slugify(article.title) })),
);

// Featured on the help center overview, as [category, article number]
const POPULAR = [
  ["Withdrawals", 1],
  ["Withdrawals", 4],
  ["Portfolio", 5],
  ["Portfolio", 3],
  ["Leaderboard", 4],
  ["Referrals", 7],
];

export const POPULAR_ARTICLES = POPULAR.map(([category, number]) => ({
  ...ARTICLE_COPY[category][number - 1],
  category,
  slug: slugify(ARTICLE_COPY[category][number - 1].title),
}));
